import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Visibility } from '@mui/icons-material';
import AdminConfirmDialog from '../Common/AdminConfirmDialog';
import { useGtmContentMutations } from '../../../hooks/gtm-content-hooks';
import { GtmContentFieldGrid, GtmOptionSources } from './GtmContentFields';
import { GtmContentSpec, gtmContentSpec, gtmRowLabel } from './gtmContentSpecs';

/**
 * The generic view/edit surface for every architecture page content table.
 *
 * `GtmContentCrud` is a table of one collection plus the dialog that edits a row of it. A spec
 * with `children` renders those child collections inside its own dialog through the same
 * component, which is how content sections reach their points, list items and sources — the
 * recursion bottoms out because leaf specs declare no children.
 *
 * Rows always come from the parent record the caller already has, so nothing here fetches; the
 * mutations invalidate the go-to-market detail and the fresh rows arrive as props.
 */

export type GtmContentPermissions = {
  readOnly?: boolean;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
};

type GtmContentCrudProps = GtmContentPermissions & {
  spec: GtmContentSpec;
  /** The owner row's id — `null` while the parent is unsaved. */
  parentId: number | null;
  rows: any[];
  optionSources: GtmOptionSources;
  /** Compact rendering for a collection nested inside a dialog. */
  dense?: boolean;
  /** Shown once under the heading. */
  description?: string;
  /** Set when the surrounding accordion already names the collection. */
  hideTitle?: boolean;
  loading?: boolean;
};

const byOrder = (rows: any[]): any[] =>
  [...(rows || [])].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0) || (a?.id ?? 0) - (b?.id ?? 0));

/** Cells hold copy, ids, enum slugs and one boolean — none of which are safe to render raw. */
const cellText = (value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return value.name || value.title || value.label || `#${value.id}`;
  const text = String(value);
  return text.length > 90 ? `${text.slice(0, 90)}…` : text;
};

const emptyForm = (spec: GtmContentSpec, rowCount: number): Record<string, any> => {
  const form: Record<string, any> = {};
  spec.fields.forEach((field) => {
    if (field.type === 'boolean') {
      form[field.key] = false;
    } else if (field.type === 'number') {
      // New rows land at the end of the list rather than colliding with the first one.
      form[field.key] = field.key === 'order' || field.key === 'step_number' ? rowCount : 0;
    } else {
      form[field.key] = '';
    }
  });
  return form;
};

const formFromRow = (spec: GtmContentSpec, row: any): Record<string, any> => {
  const form: Record<string, any> = {};
  spec.fields.forEach((field) => {
    const value = row?.[field.key];
    if (field.type === 'boolean') {
      form[field.key] = !!value;
    } else if (field.type === 'number') {
      form[field.key] = typeof value === 'number' ? value : Number(value ?? 0) || 0;
    } else if (field.type === 'select') {
      form[field.key] = value && typeof value === 'object' ? value.id ?? null : value ?? null;
    } else {
      form[field.key] = value ?? '';
    }
  });
  return form;
};

/** Optional blanks go back as `null` to match the nullable columns behind them. */
const toPayload = (
  spec: GtmContentSpec,
  form: Record<string, any>,
  parentField: string,
  parentId: number
): Record<string, any> => {
  const payload: Record<string, any> = { [parentField]: parentId };

  spec.fields.forEach((field) => {
    const value = form[field.key];

    if (field.type === 'boolean') {
      payload[field.key] = !!value;
      return;
    }
    if (field.type === 'number') {
      payload[field.key] = Number(value ?? 0) || 0;
      return;
    }
    if (field.type === 'select') {
      payload[field.key] = value === '' || value === undefined ? null : value;
      return;
    }
    const text = typeof value === 'string' ? value.trim() : value;
    payload[field.key] = text === '' || text === undefined ? (field.required ? '' : null) : text;
  });

  return payload;
};

const missingRequired = (spec: GtmContentSpec, form: Record<string, any>): string[] =>
  spec.fields
    .filter((field) => {
      if (!field.required) return false;
      const value = form[field.key];
      if (field.type === 'number' || field.type === 'boolean') return false;
      return value === null || value === undefined || String(value).trim() === '';
    })
    .map((field) => field.label);

const GtmContentEditorDialog: React.FC<
  GtmContentPermissions & {
    spec: GtmContentSpec;
    open: boolean;
    mode: 'create' | 'edit';
    parentId: number;
    /** The live row from the parent payload, so child lists refresh after their own writes. */
    row: any | null;
    rowCount: number;
    optionSources: GtmOptionSources;
    onClose: () => void;
  }
> = ({
  spec,
  open,
  mode,
  parentId,
  row,
  rowCount,
  optionSources,
  onClose,
  readOnly = false,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}) => {
  const isEdit = mode === 'edit' && !!row?.id;
  const { create, update, isSaving } = useGtmContentMutations(spec);

  const [form, setForm] = useState<Record<string, any>>(() => emptyForm(spec, rowCount));
  const [initialForm, setInitialForm] = useState<Record<string, any>>(() =>
    emptyForm(spec, rowCount)
  );
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset only when the dialog opens on a different row: a child write refetches the parent and
  // hands back a new `row` object, which must not wipe what's being typed here.
  useEffect(() => {
    if (!open) return;
    const next = isEdit ? formFromRow(spec, row) : emptyForm(spec, rowCount);
    setForm(next);
    setInitialForm(next);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, row?.id, spec.key]);

  const hasUnsavedChanges = useMemo(
    () => spec.fields.some((field) => form[field.key] !== initialForm[field.key]),
    [spec, form, initialForm]
  );

  const onFieldChange = (key: string, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const resetAndClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleClose = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const handleSave = async () => {
    if (readOnly) {
      resetAndClose();
      return;
    }

    const missing = missingRequired(spec, form);
    if (missing.length) {
      setSaveError(`Required: ${missing.join(', ')}`);
      return;
    }

    const payload = toPayload(spec, form, spec.parentField, parentId);

    try {
      if (isEdit) {
        await update.mutateAsync({ ...payload, id: row.id });
      } else {
        await create.mutateAsync(payload);
      }
      resetAndClose();
    } catch (error: any) {
      setSaveError(error?.message || 'Save failed.');
    }
  };

  const childSpecs = (spec.children || []).map(gtmContentSpec);
  const title = readOnly
    ? `View ${spec.singular}${isEdit ? `: ${gtmRowLabel(spec, row)}` : ''}`
    : isEdit
      ? `${spec.singular}: ${gtmRowLabel(spec, row)}`
      : `New ${spec.singular}`;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography variant="h6">{title}</Typography>
            {isSaving && <CircularProgress size={18} />}
          </Box>
        </DialogTitle>

        <DialogContent>
          <GtmContentFieldGrid
            fields={spec.fields}
            form={form}
            onFieldChange={onFieldChange}
            disabled={readOnly || !canUpdate}
            optionSources={optionSources}
          />

          {!!saveError && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {saveError}
            </Typography>
          )}

          {childSpecs.length > 0 && (
            <Box mt={3}>
              <Divider sx={{ mb: 2 }} />
              {!isEdit ? (
                <Typography variant="body2" color="text.secondary">
                  {`Save the ${spec.singular.toLowerCase()} first to manage its ${childSpecs
                    .map((child) => child.title.toLowerCase())
                    .join(', ')}.`}
                </Typography>
              ) : (
                childSpecs.map((child) => (
                  <Box key={child.key} mb={2}>
                    <GtmContentCrud
                      spec={child}
                      parentId={row.id}
                      rows={row?.[child.rowsProperty] || []}
                      optionSources={optionSources}
                      readOnly={readOnly}
                      canCreate={canCreate}
                      canUpdate={canUpdate}
                      canDelete={canDelete}
                      dense
                    />
                  </Box>
                ))
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isSaving}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly && canUpdate && (
            <Button variant="contained" onClick={handleSave} disabled={isSaving}>
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              {`You have unsaved changes to this ${spec.singular.toLowerCase()}.`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you continue, your changes will be lost.
            </Typography>
          </Box>
        }
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={isSaving}
        onConfirm={resetAndClose}
        onCancel={() => setConfirmCloseOpen(false)}
      />
    </>
  );
};

const GtmContentCrud: React.FC<GtmContentCrudProps> = ({
  spec,
  parentId,
  rows,
  optionSources,
  dense = false,
  description,
  hideTitle = false,
  loading = false,
  readOnly = false,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}) => {
  const { remove, isSaving } = useGtmContentMutations(spec);

  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<any | null>(null);

  const sortedRows = useMemo(() => byOrder(rows), [rows]);
  // Look the row up on every render so a write elsewhere refreshes what the dialog shows.
  const editingRow = editingId != null ? sortedRows.find((r) => r.id === editingId) ?? null : null;

  const parentSaved = typeof parentId === 'number' && Number.isFinite(parentId);
  const effectiveCanCreate = parentSaved && !readOnly && canCreate;
  const effectiveCanUpdate = !readOnly && canUpdate;
  const effectiveCanDelete = !readOnly && canDelete;

  const closeDialog = () => {
    setDialogMode(null);
    setEditingId(null);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    await remove.mutateAsync(pendingDelete.id);
    setPendingDelete(null);
  };

  const body = (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box>
          {!hideTitle && (
            <Typography variant={dense ? 'subtitle2' : 'h6'}>
              {spec.title}
              <Chip
                size="small"
                label={sortedRows.length}
                sx={{ ml: 1 }}
                color={sortedRows.length ? 'primary' : 'default'}
              />
            </Typography>
          )}
          {!!description && (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {(loading || isSaving) && <CircularProgress size={16} />}
          <Button
            size="small"
            variant={dense ? 'outlined' : 'contained'}
            startIcon={<Add />}
            onClick={() => {
              setEditingId(null);
              setDialogMode('create');
            }}
            disabled={!effectiveCanCreate}
          >
            Add
          </Button>
        </Box>
      </Box>

      {!parentSaved && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Save the parent record first to manage these.
        </Typography>
      )}

      <TableContainer sx={{ maxHeight: dense ? 240 : 400, mt: 1.5 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {spec.columns.map((column) => (
                <TableCell key={column.key}>{column.label}</TableCell>
              ))}
              <TableCell width={100}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.map((row) => (
              <TableRow key={row.id} hover>
                {spec.columns.map((column) => (
                  <TableCell key={column.key}>{cellText(row?.[column.key])}</TableCell>
                ))}

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title={effectiveCanUpdate ? 'Edit' : 'View'}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingId(row.id);
                        setDialogMode('edit');
                      }}
                    >
                      {effectiveCanUpdate ? (
                        <Edit fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>

                  {effectiveCanDelete && (
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setPendingDelete(row)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {!loading && sortedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={spec.columns.length + 1}>
                  <Typography variant="body2" color="text.secondary">
                    None created.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {dialogMode && parentSaved && (
        <GtmContentEditorDialog
          spec={spec}
          open
          mode={dialogMode}
          parentId={parentId as number}
          row={editingRow}
          rowCount={sortedRows.length}
          optionSources={optionSources}
          onClose={closeDialog}
          readOnly={readOnly}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      )}

      <AdminConfirmDialog
        open={!!pendingDelete}
        title={`Delete ${spec.singular}`}
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              {`Delete "${gtmRowLabel(spec, pendingDelete)}"?`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This also deletes anything nested under it, and cannot be undone.
            </Typography>
          </Box>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="error"
        loading={remove.isPending}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );

  return dense ? (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      {body}
    </Paper>
  ) : (
    <Box>{body}</Box>
  );
};

export default GtmContentCrud;
